import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Chip,
  CoupleSessionRow,
  VideoIcon,
  DrillCard,
  FriendlyError,
  PromptCard,
  SectionLoading,
} from "@codel/dova-ds";
import { useMeContext } from "../MeApp";
import { COUPLE_EVENTS } from "../analytics/events";
import { track, useTrackOnce } from "../analytics/track";
import { useMeFeatureFlags } from "../hooks/useMeFeatureFlags";
import { buildShareLinkUrl } from "../auth/meToken";
import { shareInviteLink } from "../auth/shareInvite";
import { useCheckinState } from "../hooks/reflectionHooks";
import { useReports } from "../hooks/reportHooks";
import { useActionableRescheduleOffers } from "../hooks/rescheduleHooks";
import { RescheduleOfferCtas } from "../reschedule/RescheduleOfferCtas";
import { SessionChangeActions } from "../reschedule/SessionChangeActions";
import { FALLBACK_PARTNER_NAME } from "../reschedule/rescheduleCopy";
import {
  getSessionJoinState,
  SESSION_JOIN_CAPTIONS,
} from "../sessionJoinState";
import {
  useInvalidateSessions,
  usePastSessions,
  useUpcomingSessions,
  type MeSession,
} from "../hooks/sessionHooks";
import { useInvalidateCheckin } from "../hooks/reflectionHooks";
import {
  CHECKIN_PROMPT_WINDOW_HOURS,
  FEEDBACK_PROMPT_WINDOW_HOURS,
  rankHomePrompts,
  type HomePrompt,
} from "./prompts";
import { standingSlotWeekdayLabel, UPCOMING_SECTION_COPY } from "./homeUpcomingCopy";
import { FALLBACK_THERAPIST_NAME, HOME_HUB_COPY } from "./homeHubCopy";
import { InPersonIcon } from "./InPersonIcon";
import { CouplePair } from "../onboarding/components/CouplePair";

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
// Ticking-clock cadence while a time boundary is still ahead — the
// so a hub left open updates its countdown,
// check-in window, and Join gate without a reload.
const HUB_CLOCK_MS = 15 * 1000;

/** "Thu, Sep 17" from an offset-bearing local ISO, read off its calendar date verbatim
 *  (the client's stored tz, never the browser's); null when it doesn't parse. */
function localDateLabel(isoLocal: string): string | null {
  const match = isoLocal.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
}

function formatDate(session: Pick<MeSession, "startLocal" | "tzid">): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: session.tzid,
  }).format(new Date(session.startLocal));
}

function formatDateTime(session: MeSession): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: session.tzid,
  }).format(new Date(session.startLocal));
}

/** When the Join window opens, as a time in the session's tz — the SERVER's instant.
 *  Derived here from `startUtc` minus a local constant until 2026-09-25, which is how the
 *  button came to advertise an opening time the guard and the session email disagreed with. */
function formatJoinOpensAt(session: MeSession): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: session.tzid,
  }).format(new Date(session.joinOpensAt));
}

function formatTime(session: MeSession): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: session.tzid,
  }).format(new Date(session.startLocal));
}

/** The subtitle's claim is the server's answer end to end — this picks one, it never builds one.
 *
 *  The page used to infer the weekly slot by demanding EVERY upcoming session share a weekday
 *  and time, so a single ad-hoc booking erased a real standing slot (the operator-reported bug).
 *  It then read the day off the first standing occurrence's own instant, which a RESCHEDULE
 *  moves while keeping the linkage — a Friday couple whose next beat was pushed to a Wednesday
 *  were told their standing slot was Wednesday. Deriving it from the slot instead just mirrored
 *  the bug: `update_slot` edits the rule in place and leaves generated beats alone, so an EDITED
 *  slot names a day none of the sessions below are actually on.
 *
 *  So the server now sends `standingSlotWeekday` only where its two sources AGREE, and this
 *  looks for a beat that carries one. A rescheduled beat contributes nothing and the series'
 *  remaining beats still answer; when NO beat can name a day — an edited slot nothing has been
 *  regenerated for, a slot row that no longer resolves — the subtitle is withheld entirely.
 *  Saying nothing is the only honest option: this page cannot outrank the dates beneath it.
 *
 *  `fromStandingSlot` is deliberately not consulted. A weekday is only ever sent for a standing
 *  beat, so it already implies the flag, and reading the flag FIRST would find the rescheduled
 *  beat and stop at its empty answer. */
function deriveStandingSlotWeekday(sessions: MeSession[]): string | null {
  for (const session of sessions) {
    const label = standingSlotWeekdayLabel(session.standingSlotWeekday);
    if (label) return label;
  }
  return null;
}

function promptCopy(prompt: HomePrompt): {
  eyebrow: string;
  title: string;
  meta?: string;
  actionLabel: string;
  route?: string;
  centered?: boolean;
} {
  if (prompt.kind === "invite") {
    return {
      eyebrow: HOME_HUB_COPY.inviteEyebrow,
      title: HOME_HUB_COPY.inviteTitle,
      meta: HOME_HUB_COPY.inviteMeta,
      actionLabel: HOME_HUB_COPY.inviteActionLabel,
      centered: true,
    };
  }
  if (prompt.kind === "feedback") {
    return {
      eyebrow: HOME_HUB_COPY.feedbackEyebrow,
      title: HOME_HUB_COPY.feedbackTitle,
      meta: HOME_HUB_COPY.feedbackMeta(localDateLabel(prompt.endedAtLocal), prompt.sessionNumber),
      actionLabel: HOME_HUB_COPY.feedbackActionLabel,
      route: `/me/feedback/${prompt.sessionUuid}`,
    };
  }
  if (prompt.kind === "checkin") {
    return {
      eyebrow: HOME_HUB_COPY.checkinEyebrow,
      title: HOME_HUB_COPY.checkinPendingTitle,
      meta: HOME_HUB_COPY.checkinPendingMeta,
      actionLabel: HOME_HUB_COPY.checkinPendingActionLabel,
      route: `/me/checkin/${prompt.sessionUuid}`,
    };
  }
  return {
    eyebrow: HOME_HUB_COPY.reportPromptEyebrow,
    title: prompt.title,
    meta: HOME_HUB_COPY.reportPromptMeta,
    actionLabel: HOME_HUB_COPY.reportPromptActionLabel,
    route: `/me/reports/${prompt.reportUuid}`,
  };
}

export function HomeHubPage() {
  const navigate = useNavigate();
  const { couple, partner } = useMeContext();
  const { flags } = useMeFeatureFlags();
  const [inviteCopied, setInviteCopied] = useState(false);
  const upcoming = useUpcomingSessions();
  const past = usePastSessions({ pageSize: 5 });
  const nextSession = upcoming.data?.[0] ?? null;
  const checkin = useCheckinState(nextSession?.uuid ?? "", { enabled: !!nextSession });
  const reports = useReports();
  const rescheduleOffers = useActionableRescheduleOffers();
  const isLoading =
    upcoming.isLoading || past.isLoading || checkin.isLoading || reports.isLoading || rescheduleOffers.isLoading;
  const laterUpcoming = upcoming.data?.slice(1) ?? [];
  const standingSlotWeekday = deriveStandingSlotWeekday(upcoming.data ?? []);
  const otherPartner = couple?.partners.find((candidate) => candidate.id !== partner?.id) ?? null;
  const partnerJoined = !!otherPartner?.onboardingState.completedAt;
  // The offer's counterpart is the couple's OTHER partner (dyad); fall back to a
  // generic label rather than ever rendering the raw `partner_id` (PII default).
  const reschedulePartnerName = otherPartner?.firstName?.trim() || FALLBACK_PARTNER_NAME;
  const newestUnreadReport = reports.data?.find((report) => report.isNew) ?? null;
  useTrackOnce(
    COUPLE_EVENTS.homebaseViewed,
    {
      has_next_session: !!nextSession,
      next_session_is_standing: !!nextSession?.fromStandingSlot,
      partner_joined: partnerJoined,
      past_sessions_total: past.total,
      has_unread_report: !!newestUnreadReport,
      pending_reschedule_offers: rescheduleOffers.data?.length ?? 0,
    },
    !isLoading,
  );
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Every instant at which this page renders something different: the
  // check-in window opening (start − 24h), the Join window opening (`joinOpensAt`), the
  // session start (chip flips to "Ready now", check-in closes), the Join window
  // CLOSING (`joinClosesAt`, a grace after the scheduled end — the hero advances), and
  // the feedback prompt decaying (last end + 48h). While any is ahead, tick.
  const lastEndedSession = past.sessions[0] ?? null;
  const invalidateSessions = useInvalidateSessions();
  const invalidateCheckin = useInvalidateCheckin();
  const clockBoundaries: number[] = [];
  const upcomingStartMs = nextSession ? new Date(nextSession.startUtc).getTime() : null;
  // 🔴 THE JOIN BOUNDARIES ARE THE SERVER'S TWO INSTANTS, NOT `endUtc` AND NOT A LOCAL GATE.
  // `upcomingEndMs` was `endUtc`, so the hero refetched — and the Join button vanished — the
  // moment a session passed its SCHEDULED end, while the therapist was still in the call.
  const upcomingEndMs = nextSession ? new Date(nextSession.joinClosesAt).getTime() : null;
  const upcomingJoinGateMs = nextSession ? new Date(nextSession.joinOpensAt).getTime() : null;
  const hasUpcomingJoinUrl = !!nextSession?.joinUrl;
  if (upcomingStartMs !== null) {
    clockBoundaries.push(upcomingStartMs - CHECKIN_PROMPT_WINDOW_HOURS * HOUR_MS, upcomingStartMs);
  }
  if (upcomingJoinGateMs !== null) {
    clockBoundaries.push(upcomingJoinGateMs);
  }
  if (upcomingEndMs !== null) {
    clockBoundaries.push(upcomingEndMs);
  }
  if (lastEndedSession) {
    clockBoundaries.push(new Date(lastEndedSession.endUtc).getTime() + FEEDBACK_PROMPT_WINDOW_HOURS * HOUR_MS);
  }
  const clockSignature = clockBoundaries.join(",");
  useEffect(() => {
    const boundaries = clockSignature ? clockSignature.split(",").map(Number) : [];
    const currentNowMs = Date.now();
    setNowMs(currentNowMs);
    // Crossing end_utc needs more than a re-render: the ended session only
    // leaves `upcoming` (and reaches past/feedback) through a refetch — the
    // cached read would keep it as the hero until reload.
    let gateHandled = upcomingJoinGateMs === null || currentNowMs >= upcomingJoinGateMs;
    let endHandled = upcomingEndMs === null || currentNowMs >= upcomingEndMs;
    // Crossing start_utc closes the check-in window server-side, but the
    // cached `windowOpen` boolean won't know that without a refetch.
    let startHandled = upcomingStartMs === null || currentNowMs >= upcomingStartMs;
    // Opened in-window with a pre-gate-cached null join_url: the server withholds it
    // before `joinOpensAt`, so refetch now or Join stays unavailable.
    if (gateHandled && !endHandled && !hasUpcomingJoinUrl) {
      invalidateSessions();
    }
    if (!boundaries.some((boundary) => boundary > currentNowMs)) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      const tickNowMs = Date.now();
      setNowMs(tickNowMs);
      if (!gateHandled && upcomingJoinGateMs !== null && tickNowMs >= upcomingJoinGateMs) {
        gateHandled = true;
        if (!hasUpcomingJoinUrl) {
          invalidateSessions();
        }
      }
      if (!endHandled && upcomingEndMs !== null && tickNowMs >= upcomingEndMs) {
        endHandled = true;
        invalidateSessions();
      }
      if (!startHandled && upcomingStartMs !== null && tickNowMs >= upcomingStartMs && nextSession) {
        startHandled = true;
        invalidateCheckin(nextSession.uuid);
      }
      if (!boundaries.some((boundary) => boundary > tickNowMs)) {
        window.clearInterval(timer);
      }
    }, HUB_CLOCK_MS);
    return () => window.clearInterval(timer);
  }, [
    clockSignature,
    upcomingJoinGateMs,
    upcomingEndMs,
    upcomingStartMs,
    hasUpcomingJoinUrl,
    invalidateSessions,
    invalidateCheckin,
    nextSession,
  ]);

  const prompts = rankHomePrompts({
    partnerJoined,
    lastEndedSession: past.sessions[0] ?? null,
    nextSession,
    nextSessionCheckinSubmitted: !!checkin.data?.checkin,
    nextSessionCheckinWindowOpen: !!checkin.data?.windowOpen,
    newestUnreadReport,
    now: new Date(nowMs),
  });
  const nextSessionDeltaMs = upcomingStartMs === null ? null : upcomingStartMs - nowMs;
  // Join window: [`joinOpensAt`, `joinClosesAt`) — the server's two instants, so a client
  // who dropped out can rejoin until the window closes. The upper bound keeps the button
  // honest for the refetch gap right after it does.
  const joinState = nextSession ? getSessionJoinState(nextSession, nowMs) : null;
  const joinEnabled = joinState === "open";
  const joinCaption = joinState ? SESSION_JOIN_CAPTIONS[joinState] : null;
  const hasHistory = past.total > 0;
  const therapistName = couple?.therapistName ?? FALLBACK_THERAPIST_NAME;
  const reportCountLabel = HOME_HUB_COPY.reportCountLabel(reports.total);
  const pastCountLabel = HOME_HUB_COPY.pastCountLabel(past.total);
  const pastSessionsMeta = past.sessions[0]
    ? HOME_HUB_COPY.pastSessionsMetaWithRecent(formatDate(past.sessions[0]), pastCountLabel)
    : pastCountLabel;
  const noSessionBody = past.sessions[0]
    ? HOME_HUB_COPY.noSessionBodyWithHistory(formatDate(past.sessions[0]), therapistName)
    : HOME_HUB_COPY.noSessionBodyNoHistory;
  // The chip flips to its live styling once the join window is OPEN, which is the same
  // instant the Join button unlocks — read from the server rather than recomputed from a
  // local gate, so the two cannot disagree about when "soon" began.
  const joinGateOpen = upcomingJoinGateMs !== null && nowMs >= upcomingJoinGateMs;
  const nextSessionChip =
    nextSessionDeltaMs === null
      ? null
      : joinGateOpen
        ? {
            role: "success" as const,
            label:
              nextSessionDeltaMs > 0
                ? HOME_HUB_COPY.chipStartsIn(Math.max(1, Math.ceil(nextSessionDeltaMs / MINUTE_MS)))
                : HOME_HUB_COPY.chipReadyNow,
          }
        : {
            role: "neutral" as const,
            label:
              nextSessionDeltaMs >= DAY_MS
                ? HOME_HUB_COPY.chipInDays(Math.ceil(nextSessionDeltaMs / DAY_MS))
                : nextSessionDeltaMs >= HOUR_MS
                  ? HOME_HUB_COPY.chipInHours(Math.ceil(nextSessionDeltaMs / HOUR_MS))
                  : HOME_HUB_COPY.chipInMinutes(Math.ceil(nextSessionDeltaMs / MINUTE_MS)),
          };
  const inviteUrl = buildShareLinkUrl();

  const invitePartner = async () => {
    if (!inviteUrl) return;
    const result = await shareInviteLink(inviteUrl);
    if (result === "copied") {
      setInviteCopied(true);
      window.setTimeout(() => setInviteCopied(false), 2000);
    }
  };

  return (
    <main className="flex min-h-dvh flex-col gap-6 px-6 pt-8 pb-[max(2rem,env(safe-area-inset-bottom))]">
      <header className="flex items-center justify-between gap-4">
        <div>
          <div className="dova-eyebrow mb-1">{HOME_HUB_COPY.eyebrow}</div>
          <h1 className="m-0 font-serif text-2xl font-medium leading-tight text-primary">
            {HOME_HUB_COPY.welcomeBack(partner?.firstName)}
          </h1>
        </div>
        {/* The same pair as onboarding's welcome screen, on the page rather than in a
            card. An empty seat until the other partner has finished onboarding. */}
        <CouplePair
          names={[partner?.firstName ?? "", otherPartner?.firstName ?? ""]}
          otherJoined={partnerJoined}
          ground="canvas"
          size="md"
        />
      </header>

      {isLoading ? <SectionLoading label={HOME_HUB_COPY.loadingLabel} /> : null}
      <FriendlyError detail={upcoming.error?.message ?? past.error?.message ?? checkin.error?.message ?? reports.error?.message ?? rescheduleOffers.error?.message ?? null} />

      {!isLoading ? (
        <>
          {/* Reschedule CTA sits at the top of the hub: it's the action the user
              owes on an in-flight reschedule. The session itself stays live at its
              original time during the offer window (it is NOT hidden), so it still
              appears in the upcoming list below — the CTA is the actionable prompt to
              pick/await a new time, and the durable surface either partner can find a
              pending offer on without relying on the notification. */}
          <RescheduleOfferCtas offers={rescheduleOffers.data ?? []} partnerName={reschedulePartnerName} />

          {prompts.map((prompt) => {
            if (prompt.kind === "report") {
              return (
                <DrillCard
                  key={`${prompt.kind}-${prompt.reportUuid}`}
                  label={HOME_HUB_COPY.reportPromptEyebrow}
                  title={prompt.title}
                  meta={HOME_HUB_COPY.reportPromptMeta}
                  fresh
                  onOpen={() => navigate(`/me/reports/${prompt.reportUuid}`)}
                />
              );
            }
            const copy = promptCopy(prompt);
            if (prompt.kind === "invite") {
              return (
                <section key={prompt.kind} className="flex flex-col gap-2">
                  <div className="dova-eyebrow">{copy.eyebrow}</div>
                  <Card className="flex flex-col items-center gap-4 p-6 text-center">
                    <div className="flex justify-center">
                      {/* Inside a card, so the default ground: you, plus the empty seat. */}
                      <CouplePair names={[partner?.firstName ?? ""]} otherJoined={false} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <h2 className="m-0 [font:var(--type-h2)] text-primary">{copy.title}</h2>
                      {copy.meta ? <div className="[font:var(--type-body)] text-secondary">{copy.meta}</div> : null}
                    </div>
                    {inviteUrl ? (
                      <Button full size="xl" onClick={() => void invitePartner()}>
                        {inviteCopied ? HOME_HUB_COPY.inviteLinkCopiedLabel : copy.actionLabel}
                      </Button>
                    ) : null}
                  </Card>
                </section>
              );
            }
            return (
              <PromptCard
                key={prompt.kind}
                eyebrow={copy.eyebrow}
                title={copy.title}
                meta={copy.meta}
                actionLabel={copy.actionLabel}
                onAction={() => (copy.route ? navigate(copy.route) : undefined)}
              />
            );
          })}

          <section className="flex flex-col gap-2">
            <div className="dova-eyebrow">{HOME_HUB_COPY.nextSessionEyebrow}</div>
            <Card className="flex flex-col gap-6 p-6">
              {nextSession ? (
                <>
                  <div>
                    <div className="flex items-start justify-between gap-4">
                      <h2 className="m-0 font-serif text-xl font-medium leading-tight text-primary">
                        {formatDateTime(nextSession)}
                      </h2>
                      {nextSessionChip ? (
                        <Chip role={nextSessionChip.role} className="shrink-0">
                          {nextSessionChip.label}
                        </Chip>
                      ) : null}
                    </div>
                    <p className="mt-2 flex flex-wrap items-center gap-x-2 [font:var(--type-body)] text-secondary">
                      <span>{HOME_HUB_COPY.sessionMeta(past.total + 1, therapistName)}</span>
                      <span aria-hidden="true">•</span>
                      <span className="inline-flex items-center gap-1.5">
                        {nextSession.location === "in_person" ? (
                          <>
                            <InPersonIcon size={16} />
                            {HOME_HUB_COPY.sessionLocationInPerson}
                          </>
                        ) : (
                          <>
                            <VideoIcon size={16} aria-hidden="true" />
                            {HOME_HUB_COPY.sessionLocationVideo}
                          </>
                        )}
                      </span>
                    </p>
                    {nextSession.location === "in_person" && nextSession.locationAddress ? (
                      <p className="mt-1 [font:var(--type-body)] text-secondary">{nextSession.locationAddress}</p>
                    ) : null}
                    {/* After a session ends or is canceled there is nothing to act on, so the
                        state is a detail of the session rather than a button. */}
                    {joinState === "ended" || joinState === "canceled" ? (
                      <p className="mt-1 [font:var(--type-body)] text-secondary">{joinCaption}</p>
                    ) : null}
                  </div>
                  {/* Join leads; Reschedule and Cancel share the row under it. The wait is IN the
                      Join button's label ("Join from 4:55 AM"), never a caption beneath it. */}
                  {joinState === "ended" || joinState === "canceled" ? null : (
                  <div className="flex flex-col gap-2">
                    <Button
                      full
                      size="xl"
                      disabled={!joinEnabled}
                      className={joinState === "before-gate" ? "normal-case!" : undefined}
                      onClick={() => {
                        // Join straight from the hub; there is no separate session page.
                        if (!nextSession.joinUrl) {
                          return;
                        }
                        track(COUPLE_EVENTS.sessionJoinClicked, {
                          session_id: nextSession.uuid,
                          is_standing: nextSession.fromStandingSlot,
                          embedded: !!flags.homebase_embedded_call,
                        });
                        // Flag ON → the in-app embedded call. OFF (dark default) →
                        // open the video link in a new tab.
                        if (flags.homebase_embedded_call) {
                          navigate(`/me/sessions/${nextSession.uuid}/call`);
                        } else {
                          window.open(nextSession.joinUrl, "_blank", "noopener,noreferrer");
                        }
                      }}
                    >
                      {joinState === "before-gate"
                        ? HOME_HUB_COPY.joinOpensAt(formatJoinOpensAt(nextSession))
                        : joinState === "unavailable"
                          ? joinCaption
                          : HOME_HUB_COPY.joinSessionLabel}
                    </Button>
                    {nextSession.status === "confirmed" || nextSession.status === "tentative" ? (
                      <SessionChangeActions session={nextSession} />
                    ) : null}
                  </div>
                  )}
                </>
              ) : (
                <div>
                  <h2 className="m-0 font-serif text-xl font-medium leading-tight text-primary">
                    {HOME_HUB_COPY.nothingBookedTitle}
                  </h2>
                  <p className="mt-2 [font:var(--type-body)] text-secondary">{noSessionBody}</p>
                  <p className="mt-2 [font:var(--type-body)] text-secondary">{HOME_HUB_COPY.findTimeBody}</p>
                </div>
              )}
            </Card>
          </section>

          {laterUpcoming.length > 0 && partnerJoined ? (
            <section className="flex flex-col gap-2">
              <div className="dova-eyebrow">{UPCOMING_SECTION_COPY.sectionEyebrow}</div>
              {standingSlotWeekday ? (
                <p data-testid="standing-slot-subtitle" className="m-0 [font:var(--type-body)] text-secondary">
                  {UPCOMING_SECTION_COPY.standingSlotSubtitle(standingSlotWeekday, therapistName)}
                </p>
              ) : null}
              <Card className="overflow-hidden px-4">
                {laterUpcoming.map((session, index) => {
                  const sessionNumber = past.total + index + 2;
                  return (
                    <CoupleSessionRow
                      key={session.uuid}
                      number={sessionNumber}
                      date={formatDate(session)}
                      time={formatTime(session)}
                      action={{
                        label: UPCOMING_SECTION_COPY.rescheduleLabel,
                        ariaLabel: UPCOMING_SECTION_COPY.rescheduleAriaLabel(sessionNumber),
                        onClick: () => navigate(`/me/sessions/${session.uuid}/reschedule`),
                      }}
                    />
                  );
                })}
              </Card>
            </section>
          ) : null}

          {/* Each archive gates on ITS OWN count — a couple can hold letters
              without counted sessions (or vice versa), and the drill card is
              the archive's only entry point once the unread prompt is gone. */}
          {reports.total > 0 ? (
            <DrillCard
              label={HOME_HUB_COPY.reportsLabel}
              title={HOME_HUB_COPY.reportsTitle}
              meta={HOME_HUB_COPY.reportsMeta(reportCountLabel)}
              onOpen={() => navigate("/me/reports")}
              actionLabel={HOME_HUB_COPY.viewAllLabel}
            />
          ) : null}
          {hasHistory ? (
            <DrillCard
              label={HOME_HUB_COPY.pastSessionsLabel}
              title={HOME_HUB_COPY.pastSessionsTitle(past.total)}
              meta={pastSessionsMeta}
              onOpen={() => navigate("/me/sessions/past")}
              actionLabel={HOME_HUB_COPY.viewAllLabel}
            />
          ) : null}
        </>
      ) : null}
    </main>
  );
}
